import React from 'react';
import { Scenario, Category, TestResult } from '../../types';

export interface TestManagerScenariosListProps {
  currentCategory: string;
  categories: Category[];
  currentScenario: Scenario | null;
  currentTest: TestResult;
  onScenarioSelect: (scenario: Scenario) => void;
  onBulkStatusPassed: () => void;
  onBulkStatusNotTested: () => void;
}

export const TestManagerScenariosList: React.FC<TestManagerScenariosListProps> = ({
  currentCategory,
  categories,
  currentScenario,
  currentTest,
  onScenarioSelect,
  onBulkStatusPassed,
  onBulkStatusNotTested,
}) => {
  const category = categories.find((cat) => cat.name === currentCategory);
  const scenarios = category?.scenarios.filter((s) => s.isDeleted !== 'true') ?? [];

  if (!currentCategory) {
    return (
      <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-6 transform transition-all duration-200 hover:shadow-2xl">
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">Wybierz kategorię, aby zobaczyć scenariusze</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-6 transform transition-all duration-200 hover:shadow-2xl">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        {currentCategory}
      </h2>

      <div className="bg-gray-50 dark:bg-slate-600/50 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{scenarios.length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Wszystkie</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
              {scenarios.filter((s) => s.Status === 'Passed').length}
            </div>
            <div className="text-sm text-green-600 dark:text-green-400">Zaliczone</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400 mb-1">
              {scenarios.filter((s) => s.Status === 'Failed').length}
            </div>
            <div className="text-sm text-red-600 dark:text-red-400">Niezaliczone</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">
              {scenarios.filter((s) => s.Status === 'Blocked').length}
            </div>
            <div className="text-sm text-yellow-600 dark:text-yellow-400">Zablokowane</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Scenariusze testowe</h3>
        <div className="flex gap-2">
          <button
            onClick={onBulkStatusPassed}
            className="px-3 py-2 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60 rounded-lg transition-all flex items-center gap-1 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            All Passed
          </button>
          <button
            onClick={onBulkStatusNotTested}
            className="px-3 py-2 bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-500 rounded-lg transition-all flex items-center gap-1 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            All Not Tested
          </button>
        </div>
      </div>

      <div className="space-y-3 h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar pr-2">
        {scenarios.map((scenario, index) => {
          const testResult = currentTest.scenarioResults[scenario['ID testu']];
          const scenarioWithNotes = {
            ...scenario,
            Notes: testResult?.Notes ?? scenario.Notes ?? '',
          };
          const isSelected = currentScenario?.['ID testu'] === scenario['ID testu'];
          const uniqueKey = `${currentCategory}-${scenario['ID testu']}-${index}`;

          return (
            <div
              key={uniqueKey}
              className={`rounded-xl border transition-all duration-200 transform hover:-translate-y-1 ${
                isSelected
                  ? 'border-blue-200 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:shadow-md'
              }`}
            >
              <button
                onClick={() => onScenarioSelect(scenarioWithNotes)}
                className="w-full text-left p-4"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">{scenario['Nazwa testu']}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-300">ID: {scenario['ID testu']}</p>
                  </div>
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${
                      scenario.Status === 'Passed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : scenario.Status === 'Failed'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                          : scenario.Status === 'Blocked'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-slate-600 dark:text-gray-200'
                    }`}
                  >
                    {scenario.Status === 'Passed' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {scenario.Status === 'Failed' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {scenario.Status === 'Blocked' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    {(!scenario.Status || scenario.Status === 'Not Tested') && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {scenario.Status || 'Not Tested'}
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
